import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Tag } from 'lucide-react';
import { Button, Sheet } from '@ai-tutor/ui';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { lessonApi } from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';

/**
 * Multi-select tag filter that sits beside ViewFilter on My Learning.
 * Tags are aggregated from BOTH lessons and folders via /lessons/tags
 * (combined counts). Selecting multiple tags performs OR matching on
 * the server.
 *
 * Selection cue is the indigo wash + text colour - no tick icon, on
 * purpose. The colour shift is the affordance.
 *
 * Below `sm` the list opens as a bottom sheet with a Done button. It is
 * multi-select, so it stays open while you pick, and as a dropdown it sat
 * over the lesson cards it was filtering - their menus under it, and the
 * results it was changing out of sight.
 */

interface TagFilterProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Disable while the page is mid-fetch to avoid stacked in-flight requests. */
  disabled?: boolean;
}

export function TagFilter({ value, onChange, disabled }: TagFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const phone = useMediaQuery('(max-width: 639.98px)');
  const containerRef = useRef<HTMLDivElement>(null);

  const tagsQuery = useQuery({
    queryKey: queryKeys.lessons.tags(),
    queryFn: () => lessonApi.getMyLearningTags(),
    staleTime: 60_000,
  });

  const options = tagsQuery.data?.tags ?? [];
  const selectedSet = useMemo(() => new Set(value), [value]);
  const isFiltered = value.length > 0;

  useEffect(() => {
    if (!isOpen || phone) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, phone]);

  const triggerLabel =
    value.length === 0
      ? 'Tags'
      : value.length === 1
        ? value[0]
        : `${value.length} tags`;

  const toggle = (tag: string) => {
    if (selectedSet.has(tag)) {
      onChange(value.filter(t => t !== tag));
    } else {
      onChange([...value, tag]);
    }
  };

  const list = (
    // `role='group'`, not `role='listbox'`. The popup previously claimed to
    // be a listbox whose options were `<button role='option'>`, which axe
    // rejects twice over: a listbox may not contain buttons
    // (aria-required-children), and it had no accessible name
    // (aria-input-field-name). These are toggles, not a single-selection
    // list — each one independently switches a filter on or off — so a
    // labelled group of `aria-pressed` buttons is both valid and the
    // truthful description. It also keeps native button keyboard handling,
    // which a `<div role='option'>` would have had to reimplement.
    <div
      role='group'
      aria-label='Filter by tags'
      className={cn(
        // Below `md` the trigger is `static`, so this anchors to the
        // header's filter row and spans it - a right-anchored 260px box
        // off a left-hand chip ran off the left edge of a phone. In the
        // phone sheet it is just the sheet's body.
        !phone &&
          'absolute top-full z-50 mt-2 rounded-2xl border border-border bg-card shadow-lg max-md:inset-x-0 md:right-0 md:w-[260px]'
      )}
    >
      <div
        className={cn(
          'space-y-1 overflow-y-auto p-1.5',
          phone ? 'px-3' : 'max-h-72'
        )}
      >
        {tagsQuery.isLoading ? (
          <div className='px-3 py-6 text-center text-sm text-muted-foreground'>
            Loading tags…
          </div>
        ) : options.length === 0 ? (
          <div className='px-3 py-6 text-center text-sm text-muted-foreground'>
            No tags yet
          </div>
        ) : (
          options.map(option => {
            const isActive = selectedSet.has(option.value);
            return (
              <button
                key={option.value}
                type='button'
                aria-pressed={isActive}
                onClick={() => toggle(option.value)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition-colors max-sm:min-h-11',
                  !isActive && 'hover:bg-muted dark:hover:bg-accent',
                  isActive && 'bg-brand-50/70 dark:bg-tint-brand'
                )}
              >
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate text-sm font-medium',
                    isActive
                      ? 'text-brand-700 dark:text-tint-brand-foreground'
                      : 'text-foreground'
                  )}
                >
                  {option.value}
                </span>
                <span
                  className={cn(
                    // Selection changes only the count COLOUR; badge bg constant.
                    'flex-shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs',
                    isActive
                      ? 'text-brand-600 dark:text-primary'
                      : 'text-muted-foreground'
                  )}
                >
                  {option.count}
                </span>
              </button>
            );
          })
        )}
      </div>

      {isFiltered && (
        <div className='border-t border-border p-1.5'>
          <button
            type='button'
            onClick={() => onChange([])}
            className='w-full rounded-xl px-3 py-1.5 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground dark:hover:bg-accent max-sm:min-h-11'
          >
            Clear selection
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div ref={containerRef} className='relative inline-block max-md:static'>
      <button
        type='button'
        onClick={() => !disabled && setIsOpen(o => !o)}
        disabled={disabled}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        title='Filter by tags'
        className={cn(
          'flex h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors sm:h-10',
          'border-border bg-card text-foreground',
          'hover:bg-muted dark:hover:bg-accent',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:focus-visible:ring-primary/40',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isFiltered &&
            'dark:tint-brand border-brand-300 bg-brand-50/80 text-brand-700 hover:bg-brand-100 dark:border-primary/60 dark:hover:bg-primary/20'
        )}
      >
        <Tag className='h-4 w-4 flex-shrink-0' />
        <span className='max-w-[120px] truncate'>{triggerLabel}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform duration-200',
            isOpen ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>

      {phone ? (
        <Sheet
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title='Tags'
          size='auto'
        >
          {list}
          <div className='px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2'>
            <Button className='h-11 w-full' onClick={() => setIsOpen(false)}>
              Done
            </Button>
          </div>
        </Sheet>
      ) : (
        isOpen && list
      )}
    </div>
  );
}
